import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MessageSquare, Send } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/context/LanguageContext";

const Contact = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Security: Validate input lengths to prevent DoS/long URL issues
    if (
      formData.name.length > 100 ||
      formData.email.length > 255 ||
      formData.service.length > 100 ||
      formData.message.length > 5000
    ) {
      toast({
        title: "Input too long",
        description: "Please shorten your message.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    toast({
      title: t("contact.toast.title"),
      description: t("contact.toast.description"),
      duration: 5000,
    });

    const subject = `Contact Form: ${formData.service || 'General Inquiry'}`;
    const body = `Name: ${formData.name}\nEmail: ${formData.email}\nService: ${formData.service}\n\nMessage:\n${formData.message}`;
    window.location.href = `mailto:kevin.rassner@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const CharacterCount = ({ current, max, id }: { current: number; max: number; id: string }) => {
    const isNearLimit = current > max * 0.9;
    const isAtLimit = current >= max;

    let colorClass = "text-muted-foreground";
    if (isAtLimit) colorClass = "text-destructive font-medium";
    else if (isNearLimit) colorClass = "text-orange-500";

    return (
      <span
        id={id}
        className={`text-xs transition-colors duration-300 ${colorClass}`}
        aria-live={isNearLimit ? "polite" : "off"}
      >
        {current}/{max}
      </span>
    );
  };

  return (
    <section id="contact" className="py-12 px-4 sm:px-6 relative">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center mb-12 sm:mb-16 space-y-4 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <MessageSquare className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold">
            {t("contact.heading")}
          </h2>
          <p className="text-xl text-muted-foreground">
            {t("contact.description")}
          </p>
        </div>

        <Card className="bg-gradient-card backdrop-blur-sm border border-primary/10 p-4 md:p-12 animate-scale-in">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label htmlFor="name" className="text-sm font-medium">
                    {t("contact.form.name")}{" "}<span className="text-destructive ml-1" aria-hidden="true">*</span>
                  </label>
                  <CharacterCount id="name-count" current={formData.name.length} max={100} />
                </div>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t("contact.form.namePlaceholder")}
                  required
                  maxLength={100}
                  className="bg-background/50 border-primary/20 focus:border-primary"
                  aria-describedby="name-count"
                />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label htmlFor="email" className="text-sm font-medium">
                    {t("contact.form.email")}{" "}<span className="text-destructive ml-1" aria-hidden="true">*</span>
                  </label>
                  <CharacterCount id="email-count" current={formData.email.length} max={255} />
                </div>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t("contact.form.emailPlaceholder")}
                  required
                  maxLength={255}
                  className="bg-background/50 border-primary/20 focus:border-primary"
                  aria-describedby="email-count"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label htmlFor="service" className="text-sm font-medium">
                  {t("contact.form.service")}
                </label>
                <CharacterCount id="service-count" current={formData.service.length} max={100} />
              </div>
              <Input
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                placeholder={t("contact.form.servicePlaceholder")}
                maxLength={100}
                className="bg-background/50 border-primary/20 focus:border-primary"
                aria-describedby="service-count"
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label htmlFor="message" className="text-sm font-medium">
                  {t("contact.form.message")}{" "}<span className="text-destructive ml-1" aria-hidden="true">*</span>
                </label>
                <CharacterCount id="message-count" current={formData.message.length} max={5000} />
              </div>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder={t("contact.form.messagePlaceholder")}
                required
                rows={6}
                maxLength={5000}
                className="bg-background/50 border-primary/20 focus:border-primary resize-none"
                aria-describedby="message-count"
              />
            </div>

            <Button
              type="submit"
              variant="hero"
              size="lg"
              className="w-full group"
            >
              <Mail className="mr-2 h-5 w-5" />
              {t("contact.form.submit")}
              <Send className="ml-2 h-4 w-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Button>
          </form>
        </Card>
      </div>
    </section>
  );
};

export default Contact;
